import { consentModeDefault, dataLayer } from './consentMode';
import { containerId, measurementOn } from './market';

export { containerId };

/**
 * DIT-513 — the tag manager container.
 *
 * Loaded from the client entry rather than written into `index.html`. Two
 * reasons, and the second is the one that matters here: every marketing route is
 * prerendered, so a container id baked into the shell would ship in the static
 * HTML with no way to vary it per environment — and the funnel is client-side
 * rendered from the same shell, so one load covers both route trees.
 *
 * The container itself sets no cookie. What it fires is governed by the consent
 * state pushed before it, which is why `consentModeDefault()` runs first and in
 * the same function: separating them is how the default ends up racing the
 * container in a later refactor.
 */

const SRC = 'https://www.googletagmanager.com/gtm.js?id=';

let started = false;

/**
 * Push the denied default, then load the container. Safe to call twice.
 *
 * Returns whether it did anything, which is what the tests asserted on before
 * anyone could look at a network tab.
 */
export function initTagManager(): boolean {
  if (started || typeof window === 'undefined' || typeof document === 'undefined') return false;

  const id = containerId();
  // No id configured — a developer's checkout, or an environment where marketing
  // has not asked for measurement. Nothing loads, and nothing pretends to.
  if (!id) return false;
  // Configured, but measurement is not switched on for this market yet (./market).
  if (!measurementOn()) return false;

  started = true;

  // Before the script tag, always. A container that loads first runs its tags
  // once with no consent state, and Google reads a missing state as granted.
  consentModeDefault();

  dataLayer()?.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `${SRC}${encodeURIComponent(id)}`;
  document.head.appendChild(script);
  return true;
}
